<template>
	<v-row class="full-height payment-terms-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="payment-terms-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchKeywords"
								placeholder="Search payment terms..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="payment-terms-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Delete Button (visible when items selected) -->
						<v-col cols="auto" v-if="selectedItems.length > 0" class="mr-2">
							<v-btn
								depressed
								rounded
								color="error"
								@click="deleteWarning = true"
							>
								<v-icon left>mdi-delete</v-icon>
								Delete ({{ selectedItems.length }})
							</v-btn>
						</v-col>

						<!-- New Payment Term Button -->
						<v-col cols="auto">
							<v-btn
								depressed
								rounded
								color="success"
								@click="newPaymentTerm()"
								:disabled="activeEdit !== null"
							>
								New Payment Term
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 payment-terms-table-container">
			<v-data-table
				:headers="headers"
				:items="paymentTerms"
				:loading="$apollo.queries.paymentTerms.loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="payment-terms-data-table"
				fixed-header
				show-select
				v-model="selectedItems"
				item-key="paymentTermsId"
				:disable-sort="activeEdit !== null"
			>
				<!-- Custom body for drag-and-drop support -->
				<template v-slot:body="{ items }">
					<tbody
						@drop="onDrop($event)"
						@dragover.prevent="onDragOver($event)"
						@dragenter.prevent
					>
						<tr
							v-for="item in items"
							:key="item.paymentTermsId"
							:data-id="item.paymentTermsId"
							:draggable="activeEdit !== null"
							:class="{ dragging: dragItem === item, 'drag-over': dragOverItem === item }"
							@dragstart="onDragStart($event, item)"
							@dragend="onDragEnd"
						>
							<td>
								<v-simple-checkbox
									v-if="item.paymentTermsId !== null"
									:value="selectedItems.includes(item)"
									@input="toggleSelection(item, $event)"
									color="accent"
									:disabled="activeEdit !== null"
								></v-simple-checkbox>
							</td>
							<td>
								<template v-if="activeEdit && activeEdit.paymentTermsId === item.paymentTermsId">
									<v-text-field
										v-model="activeEdit.paymentTermsName"
										outlined
										dense
										hide-details
										@keyup.enter="savePaymentTerm()"
										@keyup.esc="cancelEdit()"
										autofocus
									></v-text-field>
								</template>
								<template v-else>
									{{ item.paymentTermsName }}
								</template>
							</td>
							<td class="text-center sequence-cell">
								<v-icon
									v-if="activeEdit !== null"
									class="drag-handle"
									style="font-size: 2rem; cursor: grab"
								>
									mdi-drag-vertical
								</v-icon>
								<span v-else>{{ item.sequence }}</span>
							</td>
							<td class="text-end">
								<template v-if="activeEdit && activeEdit.paymentTermsId === item.paymentTermsId">
									<v-btn icon color="error" @click="cancelEdit()" title="Cancel">
										<v-icon style="font-size: 2rem">mdi-close</v-icon>
									</v-btn>
									<v-btn icon color="success" @click="savePaymentTerm()" title="Save">
										<v-icon style="font-size: 2rem">mdi-check</v-icon>
									</v-btn>
								</template>
								<template v-else>
									<v-btn
										icon
										color="primary"
										@click="editPaymentTerm(item)"
										:disabled="activeEdit !== null"
										title="Edit"
									>
										<v-icon style="font-size: 2rem">mdi-pencil</v-icon>
									</v-btn>
								</template>
							</td>
						</tr>
					</tbody>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center py-8">
						<v-icon size="64" color="grey lighten-1">mdi-cash-multiple</v-icon>
						<p class="mt-4 text-h6 grey--text">No payment terms found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete the selected payment terms?'"
			:itemList="selectedItems"
			:itemListKey="'paymentTermsName'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deletePaymentTerm();
				deleteWarning = false;
			"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import { getApiUrl } from "@/helpers";

export default {
	name: "PaymentTerms",
	components: {
		WarningModal,
	},
	data() {
		return {
			searchKeywords: "",
			searchTimeout: null,
			paymentTerms: [],
			selectedItems: [],
			activeEdit: null,
			deleteWarning: false,
			totalCount: 0,
			dragItem: null,
			dragOverItem: null,
			originalSequences: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["sequence"],
				sortDesc: [false],
			},
			headers: [
				{ text: "Terms", value: "paymentTermsName", sortable: true, width: "70%" },
				{ text: "Sequence", value: "sequence", sortable: true, width: "15%", align: "center" },
				{ text: "", value: "actions", sortable: false, width: "15%", align: "end" },
			],
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
		};
	},
	apollo: {
		paymentTerms: {
			query: gql`
				query GetPaymentTerms(
					$offset: Int!
					$take: Int!
					$order: [PaymentTermsSortInput!]
					$where: PaymentTermsFilterInput
				) {
					paymentTerms(
						skip: $offset
						take: $take
						order: $order
						where: $where
					) {
						items {
							paymentTermsId
							paymentTermsName
							sequence
						}
						totalCount
					}
				}
			`,
			variables() {
				const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
				const keyword = (this.searchKeywords || "").trim();

				let order;
				if (sortBy.length > 0) {
					const direction = sortDesc[0] ? "DESC" : "ASC";
					order = [{ [sortBy[0]]: direction }];
				} else {
					// Default sort by sequence when no sort specified
					order = [{ sequence: "ASC" }];
				}

				// Build where clause - only include search filter if there's a search term
				let where = null;
				if (keyword) {
					where = {
						paymentTermsName: { contains: keyword },
					};
				}

				return {
					offset: (page - 1) * itemsPerPage,
					take: itemsPerPage,
					order,
					where,
				};
			},
			update(data) {
				this.totalCount = data.paymentTerms.totalCount;
				return data.paymentTerms.items;
			},
			notifyOnNetworkStatusChange: true,
			debounce: 400,
		},
	},
	methods: {
		toggleSelection(item, isSelected) {
			if (isSelected) {
				if (!this.selectedItems.includes(item)) {
					this.selectedItems.push(item);
				}
			} else {
				this.selectedItems = this.selectedItems.filter((i) => i !== item);
			}
		},

		newPaymentTerm() {
			const newPaymentTerm = {
				paymentTermsId: null,
				paymentTermsName: "",
				sequence: this.paymentTerms.length > 0 ? Math.max(...this.paymentTerms.map((t) => t.sequence)) + 1 : 1,
			};

			this.paymentTerms.unshift(newPaymentTerm);
			this.activeEdit = newPaymentTerm;
		},

		editPaymentTerm(item) {
			this.activeEdit = { ...item };
		},

		cancelEdit() {
			if (this.activeEdit && !this.activeEdit.paymentTermsId) {
				// Remove the new item if canceling creation
				this.paymentTerms = this.paymentTerms.filter((t) => t.paymentTermsId !== null);
			}
			this.activeEdit = null;
			this.originalSequences = null;
			this.$apollo.queries.paymentTerms.refetch();
		},

		async deletePaymentTerm() {
			try {
				for (const term of this.selectedItems) {
					await this.$http.delete(getApiUrl(`/api/settings/payment-terms/${term.paymentTermsId}`));
				}

				this.selectedItems = [];
				this.$apollo.queries.paymentTerms.refetch();
			} catch (error) {
				console.error("Error deleting payment terms:", error);
			}
		},

		async savePaymentTerm() {
			try {
				if (!this.activeEdit.paymentTermsName || !this.activeEdit.paymentTermsName.trim()) {
					return;
				}

				let res;
				if (this.activeEdit.paymentTermsId) {
					res = await this.$http.put(getApiUrl("/api/settings/payment-terms"), this.activeEdit);
				} else {
					res = await this.$http.post(getApiUrl("/api/settings/payment-terms"), this.activeEdit);
				}

				// Update sequence list for newest items
				await this.updateSequenceList(res.data.paymentTermsId);

				this.activeEdit = null;
				this.$apollo.queries.paymentTerms.refetch();
			} catch (error) {
				console.error("Error saving payment term:", error);
			}
		},

		async updateSequenceList(id) {
			try {
				const { page, itemsPerPage } = this.tableOptions;
				const pageOffset = (page - 1) * itemsPerPage;

				const sequenceList = this.paymentTerms.map((obj) => {
					return {
						entityId: obj.paymentTermsId ? obj.paymentTermsId : id,
						sequence: pageOffset !== 0 ? obj.sequence + pageOffset : obj.sequence,
					};
				});

				await this.$http.put(getApiUrl("/api/settings/payment-terms/sequence"), { sequence: sequenceList });
			} catch (error) {
				console.error("Error updating sequence:", error);
			}
		},

		onSearchInput() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}

			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
			}, 400);
		},

		onSearchClear() {
			this.searchKeywords = "";
			this.tableOptions.page = 1;
		},

		// Drag and drop sorting functions
		onDragStart(event, item) {
			// Capture original sequences before drag-and-drop starts
			if (!this.originalSequences) {
				this.originalSequences = {};
				this.paymentTerms.forEach((term) => {
					if (term.paymentTermsId) {
						this.originalSequences[term.paymentTermsId] = term.sequence;
					}
				});
			}

			this.dragItem = item;
			event.dataTransfer.effectAllowed = "move";
			event.dataTransfer.setData("text/plain", item.paymentTermsId);

			// Add visual feedback
			setTimeout(() => {
				event.target.classList.add("dragging");
			}, 0);
		},

		onDragEnd(event) {
			event.target.classList.remove("dragging");
			this.dragItem = null;
			this.dragOverItem = null;
		},

		onDragOver(event) {
			event.preventDefault();
			const targetRow = event.target.closest("tr");
			if (!targetRow || !this.dragItem) return;

			const targetId = parseInt(targetRow.dataset.id);
			const targetItem = this.paymentTerms.find((t) => t.paymentTermsId === targetId);

			if (targetItem && targetItem !== this.dragItem) {
				this.dragOverItem = targetItem;

				// Get the position to insert
				const dragIndex = this.paymentTerms.findIndex((t) => t === this.dragItem);
				const targetIndex = this.paymentTerms.findIndex((t) => t === targetItem);

				if (dragIndex !== -1 && targetIndex !== -1 && dragIndex !== targetIndex) {
					// Remove the dragged item from its current position
					const [removed] = this.paymentTerms.splice(dragIndex, 1);
					// Insert at the new position
					this.paymentTerms.splice(targetIndex, 0, removed);

					// Update sequences
					this.paymentTerms.forEach((term, index) => {
						term.sequence = index + 1;
					});
				}
			}
		},

		onDrop(event) {
			event.preventDefault();
			this.dragOverItem = null;
			// The reordering is already done in onDragOver, just clean up
		},
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
};
</script>

<style lang="scss" scoped>
.payment-terms-page {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.payment-terms-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;

	.v-btn {
		text-transform: initial;
	}
}

.payment-terms-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.payment-terms-table-container {
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow: hidden;
}

.payment-terms-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 600px;
		}

		tbody tr {
			transition: background-color 0.2s;

			&:hover {
				background-color: $color-gray-light !important;
			}

			// Drag and drop styles
			&.dragging {
				opacity: 0.5;
				background-color: $color-gray-light-2 !important;
			}

			&.drag-over {
				border-top: 2px solid $color-primary;
			}
		}
	}

	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}

.drag-handle {
	cursor: grab;
	color: $color-gray-dark;

	&:hover {
		color: $color-primary;
	}

	&:active {
		cursor: grabbing;
	}
}

.sequence-cell {
	min-width: 80px;
}
</style>
